import type { BotDifficulty, MapId } from '@signalclash/shared';
import { DEFAULT_MAP_ID, MAPS, MAP_IDS } from '@signalclash/shared';
import Phaser from 'phaser';

import { analytics, bootElapsedMs } from '../analytics/Analytics.js';
import type { ClientApp } from '../game/ClientApp.js';
import { mintRoomCode } from '../multiplayer/index.js';
import {
  getDailyChallengeProgress,
  getDailyChallenges,
  getTodayUtcDateStr,
} from '../save/DailyChallenges.js';
import {
  computePersonalRecords,
  formatScalar,
  formatVictoryTime,
} from '../save/PersonalRecords.js';
import { computeProfileStats, loadProfile, recordSessionVisit } from '../save/Profile.js';
import { loadElo, loadPeakElo, tierFor, tierProgress } from '../save/Rank.js';
import { loadRows as loadTelemetryRows } from '../save/Telemetry.js';
import { getThisWeekModifier } from '../save/WeeklyModifier.js';
import { loadXpState } from '../save/Xp.js';
import { getNextTier } from '../save/CosmeticUnlocks.js';
import { appendMainMenuBackdrop } from '../ui/mainMenuBackdrop.js';
import { injectMainMenuStyles } from '../ui/mainMenuStyles.js';
import { NetworkLobby } from '../ui/NetworkLobby.js';

// V1 main menu: one obvious instant-start CTA, then optional bot/map setup.
// Tactical is the canonical default per docs/07_BOT_AI.md; Crucible is the
// player-facing default map (DEFAULT_MAP_ID); Foundry/Vault/Canyon are the
// V1/V1.5 alternatives.
//
// V4.1 — two cohorts of menu UI:
//   - First-session (`profile.totalMatches === 0`): only PLAY NOW + Settings
//     + a "Customize" expander are visible. The CrazyGames audience clicked
//     a thumbnail; every additional decision they're forced to make before
//     gameplay is a chance to bounce.
//   - Returning (`profile.totalMatches >= 1`): the full menu (online,
//     invite, map picker, difficulty picker, profile) renders as before.
//
// Every visible button emits an analytics event so the dashboard can answer
// "where do menu clicks actually go?"
//
// Markup is a DOM overlay above the Phaser canvas (Rule 7: no Phaser objects own
// HUD state, and DOM gives us free Tab/keyboard focus + screen-reader semantics).
const MENU_ID = 'main-menu';

// V4.2 — canonical CrazyGames game slug. Used to construct fallback
// invite URLs when the SDK's `inviteLink()` returns null (Basic Launch
// SDK build, older portal version, etc.). The slug is the stable
// portal identifier for this game — it does not change between
// builds. Source: the URL pattern at
// `https://www.crazygames.com/game/signal-clash`.
const CRAZYGAMES_GAME_SLUG = 'signal-clash';

const DIFFICULTIES: BotDifficulty[] = ['recruit', 'tactical', 'commander'];

const DIFFICULTY_LABELS: Record<BotDifficulty, string> = {
  recruit: 'REC',
  tactical: 'TAC',
  commander: 'CMD',
};

const DIFFICULTY_TITLES: Record<BotDifficulty, string> = {
  recruit: 'Recruit',
  tactical: 'Tactical',
  commander: 'Commander',
};

const DIFFICULTY_BLURBS: Record<BotDifficulty, string> = {
  recruit: 'New to the game · capture-only opener · no abilities',
  tactical: 'V1 default · full roster · uses abilities · builds Hub',
  commander: 'V1.5 top tier · 250 ms reaction · matchup-aware doctrines',
};

const MAP_BLURBS: Record<MapId, string> = {
  foundry: 'V1 starter · straight north/south corridor · mid camp at (1000, 1000)',
  vault: 'V1.5 second map · S-curve relay chain · twin flank lanes around mid',
  canyon: 'V1.5 third map · Z-zigzag relay chain · west & east flanks force lateral play',
  crucible: 'Default · 3000×2400 · 7-relay graph · multiple paths, real network choices',
};

export class MainMenuScene extends Phaser.Scene {
  private menuEl: HTMLDivElement | null = null;
  private botMatchBtn: HTMLButtonElement | null = null;
  private online1v1Btn: HTMLButtonElement | null = null;
  private inviteFriendBtn: HTMLButtonElement | null = null;
  private settingsBtn: HTMLButtonElement | null = null;
  private profileBtn: HTMLButtonElement | null = null;
  private customizeBtn: HTMLButtonElement | null = null;
  private onBotMatchClick: (() => void) | null = null;
  private onOnline1v1Click: (() => void) | null = null;
  private onInviteFriendClick: (() => void) | null = null;
  private onSettingsClick: (() => void) | null = null;
  private onProfileClick: (() => void) | null = null;
  private onCustomizeClick: (() => void) | null = null;
  // Difficulty buttons live in a single ButtonGroup; refs kept so we can toggle
  // the visual selected state without re-rendering the menu card.
  private difficultyBtns = new Map<BotDifficulty, HTMLButtonElement>();
  private difficultyHandlers = new Map<BotDifficulty, () => void>();
  private blurbEl: HTMLDivElement | null = null;
  private mapBtns = new Map<MapId, HTMLButtonElement>();
  private mapHandlers = new Map<MapId, () => void>();
  private mapBlurbEl: HTMLDivElement | null = null;
  private networkLobby: NetworkLobby | null = null;
  private networkUnsubSnap: (() => void) | null = null;
  private networkUnsubLifecycle: (() => void) | null = null;
  // V4.1 — first-session UI references. Hidden by default on the simplified
  // menu; revealed when the player clicks Customize.
  private setupPanelEl: HTMLDivElement | null = null;
  private onlineRowEl: HTMLDivElement | null = null;
  private utilitySeparatorEl: HTMLHRElement | null = null;
  private firstSession = false;
  private menuShownMs = 0;
  private customizeExpanded = false;

  constructor() {
    super('MainMenu');
  }

  create(): void {
    const app = this.registry.get('app') as ClientApp;
    this.cameras.main.setBackgroundColor('#0b0d10');
    injectMainMenuStyles();
    if (app.getMode() === 'network') {
      this.mountNetworkLobby(app);
    } else {
      this.mountMenu(app);
    }
    // Menu music starts on the first user gesture (the AudioContext is
    // gated until then) — the menu's Play/Settings/Quick Play clicks all
    // route through `audio.ensureRunning()` indirectly. We kick the music
    // immediately so as soon as the context resumes the loop is queued.
    app.audio.ensureRunning();
    app.music.playMenu();
    // V4 portal: game is now fully loaded and interactive — signal loading
    // complete to the CrazyGames SDK so it can hide its own loading overlay.
    app.crazyGames.sdkGameLoadingStop();
    // V4.1 — analytics funnel: the moments the dashboard reads as "session
    // really started" are sdk_loading_stop + main_menu_visible. Firing both
    // here ensures the bucket lines up regardless of which the dashboard
    // considers authoritative.
    analytics.emit({ kind: 'sdk_loading_stop', elapsedMs: bootElapsedMs() });
    analytics.emit({ kind: 'main_menu_visible', elapsedMs: bootElapsedMs() });
    // V1.9 — disarm the boot watchdog. main.ts listens for this event
    // and (if it fires before the 15 s deadline) suppresses the stuck-
    // loading analytics emit. Sent via CustomEvent so main.ts stays
    // decoupled from the scene module graph.
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('signalclash:main-menu-visible'));
      } catch {
        // CustomEvent unavailable (very old browser) — non-fatal.
      }
    }
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unmountMenu());
  }

  // Network mode: the URL is the intent. Auto-join the room, show a
  // lobby card with status / copy-link / cancel / retry, and
  // transition to GameScene on the first snapshot from the server. No
  // Play / map / bot pickers — those choices belong to the room
  // creator (server-side for V2; matchmaker UX for V2.5+).
  private mountNetworkLobby(app: ClientApp): void {
    const boot = app.getNetworkBoot();
    if (boot === null) {
      // Defensive — getMode() returned 'network' so boot must be set.
      // Falling back to local mode keeps the menu usable.
      this.mountMenu(app);
      return;
    }

    const fullUrl = typeof window !== 'undefined' ? window.location.href : '';
    // Quick Play has no shareable room code — the matchmaker pairs the
    // next two waiting clients server-side. Show "Quick Play" as the
    // lobby label instead of a code so the player isn't confused.
    const lobbyRoomLabel = boot.kind === 'quickplay' ? 'Quick Play' : boot.roomCode;
    this.networkLobby = new NetworkLobby({
      roomCode: lobbyRoomLabel,
      fullUrl,
      // Quick Play has no privately-shareable URL; hide the Copy-link
      // button so the player doesn't share a link that just opens
      // someone else's queue.
      hideCopyLink: boot.kind === 'quickplay',
      // V1.7 (2.15): Disclose the server-side Quick Play bot-fill
      // window up-front so the player isn't blindsided when the timer
      // fires. Mirrors `SIGNALCLASH_QUICKPLAY_FILL_MS` (production 15
      // s, see SignalClashRoom.scheduleQuickPlayFill). Spread is
      // conditional so we don't pass `undefined` through to an
      // optional field (tsconfig has exactOptionalPropertyTypes).
      // Private rooms have no opening bot-fill — the mid-match
      // reconnect-bot path is covered by the existing opt-in
      // checkbox instead.
      ...(boot.kind === 'quickplay' ? { botFillAfterSec: 15 } : {}),
      // V2.5+ Reconnect-Bot opt-in. The lobby checkbox is presentation-
      // only — the boolean is owned by ClientApp and threaded into the
      // join options when `connect()` runs. Default OFF; we show the
      // toggle in BOTH private-room and Quick Play branches because the
      // server-side rule (BOTH must opt in) applies to both.
      allowBotReconnectFill: app.getAllowBotReconnectFill(),
      onAllowBotReconnectFillChange: (allow): void => {
        app.setAllowBotReconnectFill(allow);
      },
      // V2.5+ deferred-join. The match used to auto-start on lobby
      // mount, which meant any opt-in toggle the user clicked
      // afterwards never reached the server (the join options were
      // already locked in). With `onJoin`, the lobby waits in `idle`
      // until the user clicks "Join match" — by which time the opt-in
      // checkbox state is captured and threaded into the joinOrCreate.
      onJoin: () => {
        this.networkLobby?.setStatus('connecting');
        void app
          .startMatch()
          .then(() => {
            this.networkLobby?.setStatus('waiting');
          })
          .catch((err: unknown) => {
            this.networkLobby?.setStatus('failed', describeErr(err));
          });
      },
      onCancel: () => {
        this.cancelNetworkLobby(app);
      },
      onRetry: () => {
        const bootAgain = app.getNetworkBoot();
        if (bootAgain === null) return;
        app.activateNetworkBoot(bootAgain);
        this.networkLobby?.hide();
        this.networkLobby = null;
        this.mountNetworkLobby(app);
      },
    });
    this.networkLobby.show();

    // V4.2 — register the private room with the CrazyGames platform per
    // the multiplayer SDK contract
    // (https://docs.crazygames.com/sdk/game/#multiplayer-features).
    // Without this call, the platform doesn't know the player is in a
    // joinable room — the native invite button doesn't render, the
    // friends drawer can't show the room, and `inviteLink()` may not
    // mint a working URL. The flow:
    //
    //   1. updateRoom({roomId, isJoinable: true, inviteParams})
    //      → platform shows native invite button, friends drawer
    //        lists this room.
    //   2. When the room fills (lifecycle 'started' → see
    //      ClientApp.subscribeLifecycle), updateRoom({isJoinable: false})
    //      → platform hides the invite button.
    //   3. On endMatch (ClientApp.endMatch), leftRoom() → platform
    //      clears the room state.
    //
    // Quick Play has no shareable code (Colyseus matchmaker handles
    // pairing) — skip the room registration there.
    if (boot.kind === 'private') {
      app.crazyGames.updateRoom({
        roomId: boot.roomCode,
        isJoinable: true,
        // inviteParams are what the invitee sees on join — read on
        // their boot via `getInviteParams()` / `getInviteParam('room')`
        // (ClientApp.start handles both routes).
        inviteParams: { room: boot.roomCode },
      });
      // V4.1 — replace the iframe-served `window.location.href` with the
      // SDK-minted `www.crazygames.com/game/...` URL for the Copy /
      // Share buttons. Without this, the lobby's copy-link surface
      // sends a `signal-clash.game-files.crazygames.com/...` URL,
      // which the CrazyGames Cloudflare WAF blocks (the game-files
      // subdomain is for iframe asset serving only — direct visits
      // get the "Sorry, you have been blocked" page). The SDK URL
      // routes the invitee through the proper portal entry, which
      // then forwards `?room=XXX` via getInviteParam on load.
      //
      // Three-tier resolution:
      //   1. SDK `inviteLink()` — preferred. Returns the canonical URL
      //      including any portal-side affiliate / tracking params.
      //   2. Manual portal URL — fallback when SDK returns null. This
      //      happens on Basic Launch games where the SDK may not yet
      //      expose `inviteLink`, or when the loaded SDK version
      //      predates that method. The canonical URL format is
      //      `https://www.crazygames.com/game/<slug>?room=<code>`;
      //      CrazyGames forwards the param to the iframe, where
      //      `getInviteParam('room')` reads it on load.
      //   3. `window.location.href` (kept as the initial fullUrl in
      //      mountNetworkLobby) — only effective when off-portal, e.g.
      //      local dev or itch.io. On CrazyGames the manual fallback
      //      always overrides this.
      void app.crazyGames
        .inviteLink({ room: boot.roomCode })
        .then((sdkUrl) => {
          if (sdkUrl !== null && sdkUrl !== '') {
            this.networkLobby?.setFullUrl(sdkUrl);
            return;
          }
          if (app.crazyGames.isEnabled()) {
            this.networkLobby?.setFullUrl(
              `https://www.crazygames.com/game/${CRAZYGAMES_GAME_SLUG}?room=${encodeURIComponent(boot.roomCode)}`,
            );
          }
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn('[crazygames] inviteLink failed', err);
          // Same fallback as the null branch — a thrown SDK call is
          // functionally the same as a null return for our purposes.
          if (app.crazyGames.isEnabled()) {
            this.networkLobby?.setFullUrl(
              `https://www.crazygames.com/game/${CRAZYGAMES_GAME_SLUG}?room=${encodeURIComponent(boot.roomCode)}`,
            );
          }
        });
    }

    // V2.5 bot_filled: surface the lobby state ("Filled by bot —
    // match starting") BEFORE the first snapshot reaches us. The
    // server emits bot_filled immediately after the 15 s window
    // closes, then starts the match — so the lifecycle event arrives
    // a few ticks before the first state broadcast. The lobby
    // transition wakes the player with an honest queue label.
    this.networkUnsubLifecycle = app.humanConnection.onLifecycle((event) => {
      if (event.kind === 'bot_filled') {
        this.networkLobby?.setStatus('bot_filled');
      } else if (event.kind === 'bot_fill_failed') {
        // V1.8 — bot-fill threw on the server. Tell the player and let
        // them retry rather than stranding them in the lobby.
        this.networkLobby?.setStatus('failed', `Bot fill failed: ${event.reason}`);
      }
    });

    // Subscribe to the first snapshot — that's the moment the match
    // has both players and is running. Then jump to GameScene. The
    // same subscription unsubscribes itself.
    //
    // The actual `startMatch` call has been moved into the lobby's
    // `onJoin` callback (see above) so the user gets a chance to
    // configure opt-in before the join wire is sent.
    this.networkUnsubSnap = app.humanConnection.onSnapshot(() => {
      this.clearStaleRoomTimer();
      this.networkUnsubSnap?.();
      this.networkUnsubSnap = null;
      this.networkUnsubLifecycle?.();
      this.networkUnsubLifecycle = null;
      // V1.8 — race guard: if the player clicked Invite → Cancel rapidly,
      // the scene may have shut down before the first snapshot arrives.
      // Calling startGameScene against a torn-down Phaser scene throws
      // (Phaser asserts on systems.add for an inactive scene). Bail
      // silently so the user stays on whichever menu they navigated to.
      if (!this.scene.isActive()) return;
      this.networkLobby?.setStatus('connected');
      app.startGameScene();
    });

    // V1.8 — stale-room watchdog. If a friend clicks an expired
    // ?room=ABCD link, the client creates an empty room and waits
    // forever for nobody. Show "Room expired" after 30 s of waiting
    // for the opponent (private rooms only; Quick Play has its own
    // 15 s server-side bot-fill window which fires far earlier).
    if (boot.kind === 'private') {
      this.armStaleRoomTimer(app);
    }
  }

  private staleRoomTimer: ReturnType<typeof setTimeout> | null = null;

  private armStaleRoomTimer(app: ClientApp): void {
    this.clearStaleRoomTimer();
    this.staleRoomTimer = setTimeout(() => {
      this.staleRoomTimer = null;
      // Only act if we're still on the lobby (no snapshot received).
      if (this.networkUnsubSnap === null) return;
      if (!this.scene.isActive()) return;
      this.networkLobby?.setStatus(
        'failed',
        'Room expired — no opponent joined. Falling back to Quick Play.',
      );
      // Auto-fallback after a brief moment so the player has time to
      // read the message but isn't stranded.
      setTimeout(() => {
        if (!this.scene.isActive()) return;
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        url.searchParams.delete('room');
        url.searchParams.set('quickplay', '1');
        this.openNetworkLobby(app, url);
      }, 2500);
    }, 30_000);
  }

  private clearStaleRoomTimer(): void {
    if (this.staleRoomTimer !== null) {
      clearTimeout(this.staleRoomTimer);
      this.staleRoomTimer = null;
    }
  }

  private cancelNetworkLobby(app: ClientApp): void {
    // V4.2 — tell the CrazyGames platform we left the room before
    // tearing down the local lobby. Without this the platform still
    // shows the player as "in a joinable room" to friends, and
    // notifications continue to fire. No-op when not on portal.
    app.crazyGames.leftRoom();
    this.unmountMenu();
    app.activateLocalBoot();
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('room');
      url.searchParams.delete('quickplay');
      window.history.replaceState({}, '', url.toString());
    }
    this.mountMenu(app);
  }

  private openNetworkLobby(app: ClientApp, url: URL): void {
    if (typeof window === 'undefined') return;
    const boot = app.parseNetworkBootFromHref(url.toString());
    if (boot === null) return;
    window.history.pushState({}, '', url.toString());
    app.activateNetworkBoot(boot);
    this.unmountMenu();
    this.mountNetworkLobby(app);
  }

  private mountMenu(app: ClientApp): void {
    const root = ensureMenuRoot();
    root.innerHTML = '';
    root.style.display = 'flex';
    appendMainMenuBackdrop(root);

    // V4.1 — first-session cohort split. The Telemetry module owns the
    // local match-row store; row count == matches played. A returning
    // player on a different device skips the simplified menu the moment
    // the cloud-save hydrate populates Telemetry.
    //
    // `?firstsession=1` URL override forces the simplified view for
    // developer / QA preview without wiping localStorage. Read once per
    // mount so toggling the param in the address bar takes effect on
    // the next menu render. `?firstsession=0` forces the full view (for
    // confirming the post-first-match menu without playing through a
    // match each time).
    this.firstSession = computeFirstSessionForRender();
    this.customizeExpanded = false;
    this.menuShownMs = bootElapsedMs();

    const card = document.createElement('div');
    card.className = 'main-menu__card main-menu__card--home';
    if (this.firstSession) {
      card.classList.add('main-menu__card--first-session');
    }

    const titleWrap = document.createElement('div');
    titleWrap.className = 'main-menu__title-wrap';
    titleWrap.innerHTML = `
      <svg class="main-menu__signal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
        <path d="M8.5 16.05a6 6 0 0 1 7 0"/>
        <path d="M12 20h.01"/>
        <line x1="12" y1="3" x2="12" y2="9"/>
      </svg>
    `;

    const title = document.createElement('h1');
    title.className = 'main-menu__title main-menu__title--brand';
    title.setAttribute('aria-label', 'Signal Clash');
    title.innerHTML =
      '<span class="main-menu__title-word main-menu__title-word--blue">' +
      '<span class="main-menu__title-ghost" aria-hidden="true">SIGNAL</span>' +
      '<span class="main-menu__title-text">SIGNAL</span>' +
      '</span>' +
      '<span class="main-menu__title-word main-menu__title-word--red">' +
      '<span class="main-menu__title-ghost" aria-hidden="true">CLASH</span>' +
      '<span class="main-menu__title-text">CLASH</span>' +
      '</span>';
    titleWrap.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.className = 'main-menu__subtitle';
    subtitle.innerHTML =
      '<span class="main-menu__subtitle-bar"></span>' +
      '<span><span class="main-menu__subtitle-blue">&lt;</span> Control the Network &middot;<br>Destroy the Core <span class="main-menu__subtitle-red">&gt;</span></span>' +
      '<span class="main-menu__subtitle-bar"></span>';
    titleWrap.appendChild(subtitle);
    card.appendChild(titleWrap);

    this.botMatchBtn = document.createElement('button');
    this.botMatchBtn.type = 'button';
    this.botMatchBtn.className = 'main-menu__btn main-menu__btn--cta';
    this.botMatchBtn.setAttribute('aria-label', 'Play now');
    this.botMatchBtn.innerHTML =
      '<span class="main-menu__shimmer" aria-hidden="true"></span>' +
      '<span class="main-menu__btn-label">' +
      '<span class="main-menu__chev main-menu__chev--left" aria-hidden="true">&raquo;</span>' +
      '<span>PLAY NOW</span>' +
      '<span class="main-menu__chev main-menu__chev--right" aria-hidden="true">&laquo;</span>' +
      '</span>';
    this.onBotMatchClick = (): void => {
      // UI click sound + audio-context resume are handled by the global DOM
      // delegation set up in `ClientApp.wireUiDelegation`.
      analytics.emit({
        kind: 'menu_play_now_clicked',
        msSinceShown: this.msSinceShown(),
      });
      // .catch keeps any synchronous throw inside the async startMatch
      // (audio context creation on an unsupported browser, lifecycle
      // wiring throw) from surfacing as an unhandled rejection - the
      // CrazyGames portal samples those into its crash-rate KPI.
      void app.startMatch().catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[startMatch] failed', err);
      });
      app.startGameScene();
    };
    this.botMatchBtn.addEventListener('click', this.onBotMatchClick);

    const actionStack = document.createElement('div');
    actionStack.className = 'main-menu__primary main-menu__primary--hero';
    actionStack.appendChild(this.botMatchBtn);
    card.appendChild(actionStack);

    const setupPanel = document.createElement('div');
    setupPanel.className = 'main-menu__setup-panel';
    this.setupPanelEl = setupPanel;
    const setupGrid = document.createElement('div');
    setupGrid.className = 'main-menu__setup-grid';
    const difficultyColumn = document.createElement('div');
    difficultyColumn.className = 'main-menu__setup-column';
    const mapColumn = document.createElement('div');
    mapColumn.className = 'main-menu__setup-column';

    const diffLabel = document.createElement('div');
    diffLabel.className = 'main-menu__diff-label';
    diffLabel.textContent = 'Bot level';
    difficultyColumn.appendChild(diffLabel);

    const diffGroup = document.createElement('div');
    diffGroup.className = 'main-menu__diff-group main-menu__diff-group--difficulty';
    diffGroup.setAttribute('role', 'radiogroup');
    diffGroup.setAttribute('aria-label', 'Bot difficulty');
    for (const d of DIFFICULTIES) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'main-menu__diff-btn';
      btn.textContent = DIFFICULTY_LABELS[d];
      btn.title = `${DIFFICULTY_TITLES[d]} - ${DIFFICULTY_BLURBS[d]}`;
      btn.setAttribute('aria-label', DIFFICULTY_TITLES[d]);
      btn.setAttribute('role', 'radio');
      const handler = (): void => {
        const previous = app.getBotDifficulty();
        app.setBotDifficulty(d);
        this.applySelectedDifficulty(d);
        if (previous !== d) {
          analytics.emit({
            kind: 'menu_difficulty_changed',
            from: previous,
            to: d,
            msSinceShown: this.msSinceShown(),
          });
        }
      };
      btn.addEventListener('click', handler);
      this.difficultyBtns.set(d, btn);
      this.difficultyHandlers.set(d, handler);
      diffGroup.appendChild(btn);
    }
    difficultyColumn.appendChild(diffGroup);

    this.blurbEl = document.createElement('div');
    this.blurbEl.className = 'main-menu__diff-blurb';
    difficultyColumn.appendChild(this.blurbEl);

    // V1.5 map picker. Reuses the difficulty-picker styles for visual
    // continuity (same segmented-button pattern, same blurb cadence).
    const mapLabel = document.createElement('div');
    mapLabel.className = 'main-menu__diff-label';
    mapLabel.textContent = 'Map';
    mapColumn.appendChild(mapLabel);

    const mapGroup = document.createElement('div');
    mapGroup.className = 'main-menu__diff-group main-menu__diff-group--maps';
    mapGroup.setAttribute('role', 'radiogroup');
    mapGroup.setAttribute('aria-label', 'Map');
    for (const id of MAP_IDS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'main-menu__diff-btn';
      btn.textContent = MAPS[id].name;
      btn.title = MAP_BLURBS[id];
      btn.setAttribute('role', 'radio');
      const handler = (): void => {
        const previous = app.getCurrentMapId() ?? DEFAULT_MAP_ID;
        app.setMapId(id);
        this.applySelectedMap(id);
        if (previous !== id) {
          analytics.emit({
            kind: 'menu_map_changed',
            from: previous,
            to: id,
            msSinceShown: this.msSinceShown(),
          });
        }
      };
      btn.addEventListener('click', handler);
      this.mapBtns.set(id, btn);
      this.mapHandlers.set(id, handler);
      mapGroup.appendChild(btn);
    }
    mapColumn.appendChild(mapGroup);

    this.mapBlurbEl = document.createElement('div');
    this.mapBlurbEl.className = 'main-menu__diff-blurb';
    mapColumn.appendChild(this.mapBlurbEl);

    setupGrid.appendChild(difficultyColumn);
    setupGrid.appendChild(mapColumn);
    setupPanel.appendChild(setupGrid);

    // V2.5 Quick Play. Navigates to `/?quickplay=1` which re-boots
    // ClientApp in network mode (the constructor re-reads
    // `parseNetworkBootFromUrl`). An in-place local→network upgrade is
    // out of scope for V2.5 (Rule 10) — the navigation refresh is the
    // simplest path that ships today.
    this.online1v1Btn = document.createElement('button');
    this.online1v1Btn.type = 'button';
    this.online1v1Btn.className = 'main-menu__btn main-menu__btn--primary';
    this.online1v1Btn.setAttribute('aria-label', 'Online 1v1');
    this.online1v1Btn.innerHTML =
      '<span class="main-menu__btn-main">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/>' +
      '<line x1="13" y1="19" x2="19" y2="13"/>' +
      '<line x1="16" y1="16" x2="20" y2="20"/>' +
      '<line x1="19" y1="21" x2="21" y2="19"/>' +
      '<polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"/>' +
      '<line x1="5" y1="14" x2="9" y2="18"/>' +
      '<line x1="7" y1="17" x2="4" y2="20"/>' +
      '<line x1="3" y1="19" x2="5" y2="21"/>' +
      '</svg>' +
      '<span class="main-menu__btn-copy"><span class="main-menu__btn-label">ONLINE 1V1</span><span class="main-menu__btn-sub">Queue for a real opponent</span></span>' +
      '</span>';
    this.onOnline1v1Click = (): void => {
      if (typeof window === 'undefined') return;
      analytics.emit({
        kind: 'menu_online_clicked',
        msSinceShown: this.msSinceShown(),
      });
      // Build the new URL preserving any custom ?endpoint= override
      // and discarding any stale ?room=. We use `searchParams` so
      // this works under Vite's `base: '/SignalClash/'` GH Pages
      // shape too — `pathname` keeps the base prefix.
      const url = new URL(window.location.href);
      url.searchParams.delete('room');
      url.searchParams.set('quickplay', '1');
      this.openNetworkLobby(app, url);
    };
    this.online1v1Btn.addEventListener('click', this.onOnline1v1Click);

    this.inviteFriendBtn = document.createElement('button');
    this.inviteFriendBtn.type = 'button';
    this.inviteFriendBtn.className = 'main-menu__btn main-menu__btn--secondary main-menu__btn--red';
    this.inviteFriendBtn.setAttribute('aria-label', 'Invite Friend');
    this.inviteFriendBtn.innerHTML =
      '<span class="main-menu__btn-main">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>' +
      '<circle cx="9" cy="7" r="4"/>' +
      '<line x1="19" y1="8" x2="19" y2="14"/>' +
      '<line x1="22" y1="11" x2="16" y2="11"/>' +
      '</svg>' +
      '<span class="main-menu__btn-copy"><span class="main-menu__btn-label">INVITE FRIEND</span><span class="main-menu__btn-sub">Create an invite link</span></span>' +
      '</span>';
    this.onInviteFriendClick = (): void => {
      if (typeof window === 'undefined') return;
      analytics.emit({
        kind: 'menu_invite_clicked',
        msSinceShown: this.msSinceShown(),
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('quickplay');
      const roomCode = mintRoomCode(5);
      url.searchParams.set('room', roomCode);
      this.openNetworkLobby(app, url);
    };
    this.inviteFriendBtn.addEventListener('click', this.onInviteFriendClick);

    this.settingsBtn = document.createElement('button');
    this.settingsBtn.type = 'button';
    this.settingsBtn.className = 'main-menu__btn main-menu__btn--utility';
    this.settingsBtn.innerHTML =
      '<span class="main-menu__btn-content">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="3"/>' +
      '<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>' +
      '</svg>' +
      '<span>Settings</span>' +
      '</span>';
    this.onSettingsClick = (): void => {
      analytics.emit({ kind: 'menu_settings_opened' });
      app.settingsPanel.show();
    };
    this.settingsBtn.addEventListener('click', this.onSettingsClick);

    this.profileBtn = document.createElement('button');
    this.profileBtn.type = 'button';
    this.profileBtn.className = 'main-menu__btn main-menu__btn--utility';
    this.profileBtn.innerHTML =
      '<span class="main-menu__btn-content">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M20 21a8 8 0 0 0-16 0"/>' +
      '<circle cx="12" cy="7" r="4"/>' +
      '</svg>' +
      '<span>Profile</span>' +
      '</span>';
    this.onProfileClick = (): void => {
      analytics.emit({ kind: 'menu_profile_opened' });
      app.profilePanel.show();
    };
    this.profileBtn.addEventListener('click', this.onProfileClick);

    // Online and friend rooms stay available, but below setup so the
    // CrazyGames first-play path remains the only dominant action.
    const onlineRow = document.createElement('div');
    onlineRow.className = 'main-menu__secondary-stack';
    onlineRow.appendChild(this.online1v1Btn);
    onlineRow.appendChild(this.inviteFriendBtn);
    this.onlineRowEl = onlineRow;

    // V4.2 — returning-player two-column layout. After the profile +
    // XP panel landed the single-column menu grew tall enough that
    // players had to zoom to 75% to see Settings. The 2-col split puts
    // the retention surface (widget) next to the actions (online +
    // setup) so everything fits at 100% zoom on a standard CrazyGames
    // embed. First-session players keep the flat stack because their
    // menu has no widget and the right column would be empty.
    if (!this.firstSession) {
      card.classList.add('main-menu__card--two-col');
      const widget = this.buildReturnAndChallengesWidget();
      const mainCols = document.createElement('div');
      mainCols.className = 'main-menu__main-columns';
      const leftCol = document.createElement('div');
      leftCol.className = 'main-menu__main-col main-menu__main-col--left';
      if (widget !== null) leftCol.appendChild(widget);
      const rightCol = document.createElement('div');
      rightCol.className = 'main-menu__main-col main-menu__main-col--right';
      rightCol.appendChild(onlineRow);
      rightCol.appendChild(setupPanel);
      mainCols.appendChild(leftCol);
      mainCols.appendChild(rightCol);
      card.appendChild(mainCols);
    } else {
      card.appendChild(onlineRow);
      card.appendChild(setupPanel);
    }

    const utilSeparator = document.createElement('hr');
    utilSeparator.className = 'main-menu__separator';
    this.utilitySeparatorEl = utilSeparator;
    card.appendChild(utilSeparator);

    const utilityRow = document.createElement('div');
    utilityRow.className = 'main-menu__bottom-row';
    utilityRow.appendChild(this.settingsBtn);
    utilityRow.appendChild(this.profileBtn);
    setupPanel.appendChild(utilityRow);

    // Footer: Privacy / Terms links. CrazyGames submission requires
    // these to be reachable from the menu. Open in a new tab so the
    // game state isn't disturbed.
    const footer = document.createElement('div');
    footer.className = 'main-menu__footer';
    footer.innerHTML = `
      <span class="main-menu__footer-left">SIGNAL // LIVE</span>
      <span class="main-menu__footer-links">
        <a href="${import.meta.env.BASE_URL}privacy.html" target="_blank" rel="noopener">Privacy</a>
        <span>|</span>
        <a href="${import.meta.env.BASE_URL}terms.html" target="_blank" rel="noopener">Terms</a>
      </span>
    `;
    card.appendChild(footer);

    root.appendChild(card);
    this.menuEl = root;

    this.applySelectedDifficulty(app.getBotDifficulty());
    this.applySelectedMap(app.getCurrentMapId() ?? DEFAULT_MAP_ID);

    // V4.1 — first-session simplification. Hide every non-essential
    // surface and insert a Customize toggle that reveals them on demand.
    // The bot Match button, Settings button, and footer remain visible.
    if (this.firstSession) {
      this.applyFirstSessionSimplification(card);
    }

    // V1.8 — Fly.io free-tier scale-to-zero cold start is 5-10 s. Before
    // this preflight, the player clicked Online 1v1 and sat through a
    // 15 s Colyseus connect timeout before getting "Connection failed".
    // We probe /health up-front (2 s budget) and gate the online buttons:
    //   - healthy: full color, no copy change.
    //   - unhealthy / timeout: greyed out with a "Server warming up..."
    //     sublabel; clicking still tries (cold start may be in progress).
    this.runOnlinePreflight(app);

    analytics.emit({ kind: 'menu_shown', firstSession: this.firstSession });
  }

  // Fire-and-forget probe. The fetch is wrapped in AbortController to
  // bound the wait; signal cleanup happens on scene shutdown via
  // unmountMenu, which removes the DOM root the preflight handler reads.
  private runOnlinePreflight(app: ClientApp): void {
    if (typeof fetch === 'undefined') return;
    const endpoint = app.getNetworkEndpoint();
    let healthUrl: string;
    try {
      const url = new URL(endpoint);
      const proto = url.protocol === 'wss:' ? 'https:' : 'http:';
      healthUrl = `${proto}//${url.host}/health`;
    } catch {
      return;
    }
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => controller?.abort(), 2000);
    fetch(healthUrl, {
      method: 'GET',
      cache: 'no-store',
      ...(controller !== null ? { signal: controller.signal } : {}),
    })
      .then((res) => {
        clearTimeout(timer);
        if (!res.ok) this.applyServerWarmingState();
      })
      .catch(() => {
        clearTimeout(timer);
        this.applyServerWarmingState();
      });
  }

  private applyServerWarmingState(): void {
    if (this.online1v1Btn === null && this.inviteFriendBtn === null) return;
    // Apply only if the elements are still mounted. A fast scene
    // shutdown between preflight and resolution would otherwise reach
    // into detached DOM with no visible effect — harmless but noisy.
    const decorate = (btn: HTMLButtonElement | null, sub: string): void => {
      if (btn === null) return;
      btn.classList.add('main-menu__btn--warming');
      const subEl = btn.querySelector<HTMLSpanElement>('.main-menu__btn-sub');
      if (subEl !== null) subEl.textContent = sub;
    };
    decorate(this.online1v1Btn, 'Server warming up — click anyway');
    decorate(this.inviteFriendBtn, 'Server warming up — click anyway');
  }

  // Compute msSinceShown for menu_* events. Floors at 0 so a pathological
  // negative clock skew can't slip through validateMsSinceShown's
  // non-negative gate on the server.
  private msSinceShown(): number {
    return Math.max(0, bootElapsedMs() - this.menuShownMs);
  }

  // V1.8 — daily challenges + "Welcome back" banner. Combined into one
  // widget so the menu doesn't grow two new top-level rows. Returns null
  // if there's literally nothing to show (no return visit, no progress).
  // The cheapest D1/D7 retention nudges we can build without a server.
  //
  // V1.9 adds three returning-player surfaces stacked above the daily
  // challenges: an XP bar (level + progress to next), a win/loss strip
  // (lifetime W/L + last match result), and a streak chip when the
  // login streak ≥ 2. All are read-only and rendered from existing
  // localStorage so the widget remains layout-free of any server
  // dependencies.
  private buildReturnAndChallengesWidget(): HTMLDivElement | null {
    const today = getTodayUtcDateStr();
    const visit = recordSessionVisit(today);
    const dailyDefs = getDailyChallenges(today);
    const rows = loadTelemetryRows();
    const progress = getDailyChallengeProgress(dailyDefs, rows, today);
    const completed = progress.filter((p) => p.done).length;
    const stats = computeProfileStats(rows);
    const xp = loadXpState();
    const widget = document.createElement('div');
    widget.className = 'main-menu__widget';
    if (visit.isReturning) {
      const banner = document.createElement('div');
      banner.className = 'main-menu__widget-welcome';
      const days = visit.daysSinceLastVisit;
      const streakNote =
        visit.streakDays >= 2
          ? ` Streak: ${visit.streakDays} days.`
          : '';
      banner.textContent =
        days === 1
          ? `Welcome back — today's challenges have reset.${streakNote}`
          : `Welcome back — it's been ${days} days. New challenges await.${streakNote}`;
      widget.appendChild(banner);
      // V1.9 — fire-and-forget streak telemetry. Lets the dashboard see
      // whether the new streak surface correlates with D1/D7 lift. Done
      // outside the banner conditional so we still emit when the player
      // visits two days running and the streak just extended.
      analytics.emit({
        kind: 'login_streak',
        streakDays: visit.streakDays,
        extended: visit.streakExtended,
      });
    }
    // V2 Tempo — Commander Profile card. Replaces the V1.9 plain XP row
    // with a fancier surface that matches the menu's neon hex aesthetic:
    // big LV badge, XP bar with shimmer + next-milestone tease, a 4-cell
    // stats grid (matches / win rate / streak / fastest victory), and a
    // last-10 W/L sparkline. All read from existing localStorage; the
    // PersonalRecords helper does the aggregation.
    const records = computePersonalRecords(rows);
    const nextTier = getNextTier(stats.totalMatches);
    const xpPct = xp.xpToNextLevel > 0
      ? Math.max(0, Math.min(100, (xp.xpIntoLevel / xp.xpToNextLevel) * 100))
      : 100;
    const xpRemaining = Math.max(0, xp.xpToNextLevel - xp.xpIntoLevel);
    const nextTierLabel = nextTier !== null
      ? `Next title <strong>${nextTier.title}</strong> in ${Math.max(0, nextTier.matchThreshold - stats.totalMatches)} match${Math.max(0, nextTier.matchThreshold - stats.totalMatches) === 1 ? '' : 'es'}`
      : 'All titles unlocked';

    const elo = loadElo();
    const peakElo = loadPeakElo();
    const rankTier = tierFor(elo);
    const rankProgress = tierProgress(elo);
    const rankPct = (rankProgress.ratio * 100).toFixed(1);
    const rankNextLabel = rankProgress.nextTier !== null
      ? `${rankProgress.toNext} ELO to <strong style="color:${rankProgress.nextTier.color}">${rankProgress.nextTier.name}</strong>`
      : 'Peak tier reached';

    const card = document.createElement('div');
    card.className = 'main-menu__profile';

    // Header row: big LEVEL badge on the left, RANK chip on the right.
    // The rank chip carries its tier color + glow so the chip itself
    // sells the unlock the player is chasing.
    const headRow = document.createElement('div');
    headRow.className = 'main-menu__profile-head';
    headRow.innerHTML = `
      <div class="main-menu__profile-level">
        <span class="main-menu__profile-level-num">${xp.level}</span>
        <span class="main-menu__profile-level-word">LEVEL</span>
      </div>
      <div class="main-menu__profile-rank" data-tier="${rankTier.id}">
        <div class="main-menu__profile-rank-name" style="color:${rankTier.color};text-shadow:0 0 10px ${rankTier.glow}">${rankTier.name}</div>
        <div class="main-menu__profile-rank-elo">${elo} ELO</div>
      </div>
    `;
    card.appendChild(headRow);

    // Rank progress bar (tier color) sits BETWEEN the head and the XP
    // bar so the two progress meters are visually grouped — same
    // vertical rhythm, different tier hue.
    const rankBar = document.createElement('div');
    rankBar.className = 'main-menu__profile-rankbar-wrap';
    rankBar.innerHTML = `
      <div class="main-menu__profile-rankbar">
        <div class="main-menu__profile-rankbar-fill" style="width:${rankPct}%;background:linear-gradient(90deg, ${rankTier.color}, ${rankProgress.nextTier?.color ?? rankTier.color});box-shadow:0 0 12px ${rankTier.glow}"></div>
      </div>
      <div class="main-menu__profile-rankbar-foot">
        <span style="color:${rankTier.color}">${rankNextLabel}</span>
        ${peakElo > elo ? `<span class="main-menu__profile-rankbar-peak">Peak ${peakElo}</span>` : ''}
      </div>
    `;
    card.appendChild(rankBar);

    // XP / level row underneath.
    const xpHead = document.createElement('div');
    xpHead.className = 'main-menu__profile-xp-head';
    xpHead.innerHTML = `
      <span class="main-menu__profile-xp-label">XP</span>
      <span class="main-menu__profile-next-value">${xpRemaining} XP to level ${xp.level + 1}</span>
    `;
    card.appendChild(xpHead);

    // XP bar with shimmer + raw numbers BELOW the bar.
    const xpBar = document.createElement('div');
    xpBar.className = 'main-menu__profile-xpbar-wrap';
    xpBar.innerHTML = `
      <div class="main-menu__profile-xpbar">
        <div class="main-menu__profile-xpbar-fill" style="width:${xpPct.toFixed(1)}%"></div>
        <div class="main-menu__profile-xpbar-shimmer" style="left:${xpPct.toFixed(1)}%"></div>
      </div>
      <div class="main-menu__profile-xpbar-foot">
        <span class="main-menu__profile-xpbar-pos">${xp.xpIntoLevel} / ${xp.xpToNextLevel} XP</span>
        <span class="main-menu__profile-xpbar-tease">${nextTierLabel}</span>
      </div>
    `;
    card.appendChild(xpBar);

    // 4-cell records grid. Only rendered when the player has finished a
    // match — first-session players never see this card anyway (the
    // returns widget itself is gated upstream).
    if (stats.totalMatches > 0) {
      const winRateText = records.winRatePct === null ? '—' : `${records.winRatePct}%`;
      const fastestText = formatVictoryTime(records.fastestVictorySec);
      const streakText = records.currentWinStreak > 0
        ? `${records.currentWinStreak}W`
        : records.longestWinStreak > 0
          ? `Best ${records.longestWinStreak}W`
          : '—';
      const grid = document.createElement('div');
      grid.className = 'main-menu__profile-grid';
      grid.innerHTML = `
        <div class="main-menu__profile-cell">
          <div class="main-menu__profile-cell-num">${formatScalar(records.totalMatches)}</div>
          <div class="main-menu__profile-cell-lbl">Matches</div>
        </div>
        <div class="main-menu__profile-cell">
          <div class="main-menu__profile-cell-num main-menu__profile-cell-num--cyan">${winRateText}</div>
          <div class="main-menu__profile-cell-lbl">Win rate</div>
        </div>
        <div class="main-menu__profile-cell">
          <div class="main-menu__profile-cell-num main-menu__profile-cell-num--amber">${streakText}</div>
          <div class="main-menu__profile-cell-lbl">Streak</div>
        </div>
        <div class="main-menu__profile-cell">
          <div class="main-menu__profile-cell-num">${fastestText}</div>
          <div class="main-menu__profile-cell-lbl">Fastest win</div>
        </div>
      `;
      card.appendChild(grid);

      // Recent form sparkline — last-10 W/L as colored notches. Empty
      // notches fill to 10 so the row width never jitters.
      const form = document.createElement('div');
      form.className = 'main-menu__profile-form';
      const filledHtml = records.recentForm
        .map((r) => `<span class="main-menu__profile-form-cell main-menu__profile-form-cell--${r === 'W' ? 'w' : 'l'}"></span>`)
        .join('');
      const emptyCount = Math.max(0, 10 - records.recentForm.length);
      const emptyHtml = '<span class="main-menu__profile-form-cell main-menu__profile-form-cell--empty"></span>'.repeat(emptyCount);
      form.innerHTML = `
        <span class="main-menu__profile-form-lbl">Recent form</span>
        <span class="main-menu__profile-form-row">${filledHtml}${emptyHtml}</span>
      `;
      card.appendChild(form);
    }

    widget.appendChild(card);

    // V2 Tempo — Weekly Modifier chip. Renders below the profile card,
    // above the daily challenges. Color/glow vary per modifier so a
    // returning player can see at a glance "oh, it's Double XP week" or
    // "Mission Surge week."
    const weekly = getThisWeekModifier();
    const weeklyChip = document.createElement('div');
    weeklyChip.className = 'main-menu__weekly';
    weeklyChip.style.setProperty('--weekly-color', weekly.color);
    weeklyChip.style.setProperty('--weekly-glow', weekly.glow);
    weeklyChip.innerHTML = `
      <span class="main-menu__weekly-chip" style="color:${weekly.color};border-color:${weekly.color}">THIS WEEK</span>
      <span class="main-menu__weekly-name" style="color:${weekly.color};text-shadow:0 0 10px ${weekly.glow}">${weekly.name}</span>
      <span class="main-menu__weekly-desc">${weekly.description}</span>
    `;
    widget.appendChild(weeklyChip);
    const header = document.createElement('div');
    header.className = 'main-menu__widget-header';
    header.textContent = `Daily Challenges — ${completed}/${progress.length} complete`;
    widget.appendChild(header);
    const list = document.createElement('ul');
    list.className = 'main-menu__widget-list';
    for (const p of progress) {
      const li = document.createElement('li');
      li.className = `main-menu__widget-item${p.done ? ' main-menu__widget-item--done' : ''}`;
      const status = document.createElement('span');
      status.className = 'main-menu__widget-status';
      status.textContent = p.done ? '✔' : `${p.current}/${p.def.goal}`;
      const label = document.createElement('span');
      label.className = 'main-menu__widget-label';
      label.textContent = p.def.label;
      li.appendChild(status);
      li.appendChild(label);
      list.appendChild(li);
    }
    widget.appendChild(list);
    return widget;
  }

  // Hide secondary panels for first-session players + insert a Customize
  // expander above the Settings button. Idempotent; safe to call twice.
  private applyFirstSessionSimplification(card: HTMLDivElement): void {
    if (this.onlineRowEl !== null) this.onlineRowEl.style.display = 'none';
    if (this.setupPanelEl !== null) this.setupPanelEl.style.display = 'none';
    if (this.utilitySeparatorEl !== null) this.utilitySeparatorEl.style.display = 'none';
    // Insert a lightweight Customize toggle row between PLAY NOW and the
    // (now-hidden) setup panel. The Settings button stays visible inside
    // the setup panel's utility row, so when Customize expands the panel
    // it reveals Settings + Profile in their original positions.
    //
    // Profile button has no value on a first-session card (nothing to
    // show yet) — it's hidden until the player has at least one match.
    if (this.profileBtn !== null) this.profileBtn.style.display = 'none';

    const customizeRow = document.createElement('div');
    customizeRow.className = 'main-menu__first-session-actions';

    this.customizeBtn = document.createElement('button');
    this.customizeBtn.type = 'button';
    this.customizeBtn.className = 'main-menu__btn main-menu__btn--utility';
    this.customizeBtn.setAttribute('aria-label', 'Customize match settings');
    this.customizeBtn.innerHTML =
      '<span class="main-menu__btn-content">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<line x1="4" y1="21" x2="4" y2="14"/>' +
      '<line x1="4" y1="10" x2="4" y2="3"/>' +
      '<line x1="12" y1="21" x2="12" y2="12"/>' +
      '<line x1="12" y1="8" x2="12" y2="3"/>' +
      '<line x1="20" y1="21" x2="20" y2="16"/>' +
      '<line x1="20" y1="12" x2="20" y2="3"/>' +
      '<line x1="1" y1="14" x2="7" y2="14"/>' +
      '<line x1="9" y1="8" x2="15" y2="8"/>' +
      '<line x1="17" y1="16" x2="23" y2="16"/>' +
      '</svg>' +
      '<span>Customize</span>' +
      '</span>';
    this.onCustomizeClick = (): void => {
      analytics.emit({
        kind: 'menu_customize_expanded',
        msSinceShown: this.msSinceShown(),
      });
      this.expandFirstSessionMenu();
    };
    this.customizeBtn.addEventListener('click', this.onCustomizeClick);
    customizeRow.appendChild(this.customizeBtn);

    // Settings button is moved out of the setup panel for first-session
    // — that panel is hidden, so leaving Settings inside it would also
    // hide Settings. We re-parent it here; the unmount path uses the
    // stored handler regardless of position.
    if (this.settingsBtn !== null) customizeRow.appendChild(this.settingsBtn);

    // Insert AFTER PLAY NOW (the first .main-menu__primary--hero) and
    // BEFORE the footer.
    const footer = card.querySelector('.main-menu__footer');
    if (footer !== null) {
      card.insertBefore(customizeRow, footer);
    } else {
      card.appendChild(customizeRow);
    }
  }

  // Reveal the full setup panel + online/invite buttons + profile button.
  // Idempotent; subsequent clicks no-op.
  private expandFirstSessionMenu(): void {
    this.customizeExpanded = true;
    if (this.onlineRowEl !== null) this.onlineRowEl.style.display = '';
    if (this.setupPanelEl !== null) this.setupPanelEl.style.display = '';
    if (this.utilitySeparatorEl !== null) this.utilitySeparatorEl.style.display = '';
    if (this.profileBtn !== null) this.profileBtn.style.display = '';
    // Once expanded the Customize button has done its job — hide it so the
    // menu doesn't show a now-no-op control.
    if (this.customizeBtn !== null) this.customizeBtn.style.display = 'none';
  }

  private applySelectedDifficulty(active: BotDifficulty): void {
    for (const [d, btn] of this.difficultyBtns) {
      const selected = d === active;
      btn.classList.toggle('main-menu__diff-btn--selected', selected);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
    if (this.blurbEl !== null) this.blurbEl.textContent = DIFFICULTY_BLURBS[active];
  }

  private applySelectedMap(active: MapId): void {
    for (const [id, btn] of this.mapBtns) {
      const selected = id === active;
      btn.classList.toggle('main-menu__diff-btn--selected', selected);
      btn.setAttribute('aria-checked', selected ? 'true' : 'false');
    }
    if (this.mapBlurbEl !== null) this.mapBlurbEl.textContent = MAP_BLURBS[active];
  }

  private unmountMenu(): void {
    if (this.botMatchBtn !== null && this.onBotMatchClick !== null) {
      this.botMatchBtn.removeEventListener('click', this.onBotMatchClick);
    }
    if (this.online1v1Btn !== null && this.onOnline1v1Click !== null) {
      this.online1v1Btn.removeEventListener('click', this.onOnline1v1Click);
    }
    if (this.inviteFriendBtn !== null && this.onInviteFriendClick !== null) {
      this.inviteFriendBtn.removeEventListener('click', this.onInviteFriendClick);
    }
    if (this.settingsBtn !== null && this.onSettingsClick !== null) {
      this.settingsBtn.removeEventListener('click', this.onSettingsClick);
    }
    if (this.profileBtn !== null && this.onProfileClick !== null) {
      this.profileBtn.removeEventListener('click', this.onProfileClick);
    }
    if (this.customizeBtn !== null && this.onCustomizeClick !== null) {
      this.customizeBtn.removeEventListener('click', this.onCustomizeClick);
    }
    this.botMatchBtn = null;
    this.onBotMatchClick = null;
    this.online1v1Btn = null;
    this.onOnline1v1Click = null;
    this.inviteFriendBtn = null;
    this.onInviteFriendClick = null;
    this.settingsBtn = null;
    this.onSettingsClick = null;
    this.profileBtn = null;
    this.onProfileClick = null;
    this.customizeBtn = null;
    this.onCustomizeClick = null;
    this.setupPanelEl = null;
    this.onlineRowEl = null;
    this.utilitySeparatorEl = null;
    for (const [d, btn] of this.difficultyBtns) {
      const handler = this.difficultyHandlers.get(d);
      if (handler !== undefined) btn.removeEventListener('click', handler);
    }
    this.difficultyBtns.clear();
    this.difficultyHandlers.clear();
    this.blurbEl = null;
    for (const [id, btn] of this.mapBtns) {
      const handler = this.mapHandlers.get(id);
      if (handler !== undefined) btn.removeEventListener('click', handler);
    }
    this.mapBtns.clear();
    this.mapHandlers.clear();
    this.mapBlurbEl = null;
    if (this.menuEl !== null) {
      this.menuEl.style.display = 'none';
      this.menuEl.innerHTML = '';
    }
    this.menuEl = null;
    if (this.networkLobby !== null) {
      this.networkLobby.hide();
      this.networkLobby = null;
      // Mirror the showInviteButton call from mountNetworkLobby — the
      // portal button only makes sense while a lobby is on screen.
      try {
        (this.registry.get('app') as ClientApp).crazyGames.hideInviteButton();
      } catch {
        // Registry may be torn down during full app shutdown; non-fatal.
      }
    }
    if (this.networkUnsubSnap !== null) {
      this.networkUnsubSnap();
      this.networkUnsubSnap = null;
    }
    if (this.networkUnsubLifecycle !== null) {
      this.networkUnsubLifecycle();
      this.networkUnsubLifecycle = null;
    }
    this.clearStaleRoomTimer();
  }
}

function ensureMenuRoot(): HTMLDivElement {
  const existing = document.getElementById(MENU_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = MENU_ID;
  document.body.appendChild(div);
  return div;
}

// First-session resolution with a `?firstsession=1` URL override so
// developers / QA can preview the simplified view without wiping
// localStorage. `=1` forces ON, `=0` forces OFF, absent falls back to
// the real telemetry-count check.
function computeFirstSessionForRender(): boolean {
  if (typeof window !== 'undefined') {
    try {
      const param = new URL(window.location.href).searchParams.get('firstsession');
      if (param === '1') return true;
      if (param === '0') return false;
    } catch {
      // URL parse failure — fall through to the real check.
    }
  }
  return loadTelemetryRows().length === 0;
}

function describeErr(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
